import { TaxonomyIdItem } from "@amzn/taxonomy-lib";
import { getString } from "common/uistringlabels/uiStringUtils";
import { CatalogicalDomain, CatalogicalService } from "lib/services";
import { FeedbackModelRecord, FeedbackModelRequest, FeedbackStats } from "lib/services/catalogical-models";
import { useMemo, useState } from "react";

export interface GetFeedbackProps {
  taxonomyId: TaxonomyIdItem;
}

export interface GetUserFeedbackProps extends GetFeedbackProps {
  username: string;
}

export interface PostFeedbackProps {
  taxonomyId: TaxonomyIdItem;
  csrfToken: string;
  feedback: FeedbackModelRequest;
}

export interface PostFeedbackResponse {
  feedback: FeedbackModelRecord;
  itemStats: FeedbackStats;
}

export interface UseProductFeedbackProps {
  domain: CatalogicalDomain;
  defaultValues?: {
    itemFeedbackStats?: FeedbackStats;
    productFeedback?: FeedbackModelRecord[];
    userFeedback?: FeedbackModelRecord[];
  };
}

// limit to 500 while Catalogical works towards pagination
const FILTER_FOR_REVIEW = {
  content: "*",
  jobFamily: "*",
  limit: "500",
};

export function useProductFeedback(props: UseProductFeedbackProps) {
  const [itemFeedbackStats, setItemFeedbackStats] = useState<FeedbackStats | undefined>(
    props.defaultValues?.itemFeedbackStats
  );
  const [productFeedback, setProductFeedback] = useState<FeedbackModelRecord[]>(
    props.defaultValues?.productFeedback ?? []
  );
  const [userFeedback, setUserFeedback] = useState<FeedbackModelRecord[]>([]);
  const [error, setError] = useState<Error | undefined>(undefined);
  const [isLoadingReviews, setLoadingReviews] = useState<boolean>(false);
  const [isLoadingUserReviews, setLoadingUserReviews] = useState<boolean>(false);
  const [isLoadingPost, setLoadingPost] = useState<boolean>(false);
  const client = useMemo(() => new CatalogicalService(props.domain), [props.domain]);

  const isLoading = isLoadingReviews || isLoadingUserReviews || isLoadingPost;

  async function doGetReviews(props: GetFeedbackProps) {
    setLoadingReviews(true);

    try {
      const response = await client.getProductFeedbacks(props.taxonomyId, FILTER_FOR_REVIEW);
      if (!response) {
        const err = {
          name: getString("productReviews.postErrorName"),
          message: getString("productReviews.getErrorMessage"),
        };
        setError(err);
        setProductFeedback([]);
        setLoadingReviews(false);
        return undefined;
      }
      setError(undefined);
      setProductFeedback(response.feedback);
      setLoadingReviews(false);
      return response.feedback;
    } catch (err) {
      setError(err as Error);
      setLoadingReviews(false);
      return undefined;
    }
  }

  async function doGetUserReviews(props: GetUserFeedbackProps) {
    setLoadingUserReviews(true);
    const filter = {
      username: props.username,
    };
    try {
      const response = await client.getProductFeedbacks(props.taxonomyId, filter);
      if (!response) {
        const err = {
          name: getString("productReviews.postErrorName"),
          message: getString("productReviews.getErrorMessage"),
        };
        setError(err);
        setUserFeedback([]);
        setLoadingUserReviews(false);
        return undefined;
      }
      setError(undefined);
      setUserFeedback(response.feedback);
      setLoadingUserReviews(false);
      return response.feedback;
    } catch (err) {
      setError(err as Error);
      setLoadingUserReviews(false);
      return undefined;
    }
  }

  async function doPostFeedback(props: PostFeedbackProps) {
    setLoadingPost(true);
    try {
      const response = await client.postFeedback(props.taxonomyId, props.feedback, props.csrfToken);
      if (!response) {
        const err = {
          name: getString("productReviews.postErrorName"),
          message: getString("productReviews.postErrorMessage"),
        };
        setError(err);
        setLoadingPost(false);
        return undefined;
      }
      const itemInfo = await client.getCatalogItemByTaxonomyId(props.taxonomyId);
      setError(undefined);
      setProductFeedback([response, ...productFeedback]);
      setItemFeedbackStats(itemInfo?.item.feedback);
      setLoadingPost(false);
      return {
        feedback: response,
        itemStats: itemInfo?.item.feedback as FeedbackStats,
      };
    } catch (err) {
      setError(err as Error);
      setLoadingPost(false);
      return undefined;
    }
  }

  return {
    productFeedback,
    userFeedback,
    itemFeedbackStats,
    error,
    isLoading,
    isLoadingReviews,
    isLoadingUserReviews,
    isLoadingPost,
    doGetReviews,
    doGetUserReviews,
    doPostFeedback,
    setItemFeedbackStats,
  };
}
